import { useEffect, useMemo, useState } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';
import { useNotifications } from '@/hooks/useNotifications';
import { useNavigate, useLocation, Outlet, useBlocker } from 'react-router-dom';
import { XPProvider, useXP } from '@/context/XPContext';
import { StoryBottomBarProvider } from '@/context/StoryBottomBarContext';
import { ProfileNavGuardProvider, useProfileNavGuard } from '@/context/ProfileNavGuardContext';
import { StoryAudioProvider } from '@/context/StoryAudioContext';
import { StudentDashboardLayout } from '@/components/templates/StudentDashboardLayout';
import { StoryLayout } from '@/components/templates/StoryLayout';
import { IconSidebar } from '@/components/organisms/IconSidebar';
import { PageTransition } from '@/components/molecules/PageTransition';
import { PushReminderDrawer } from '@/components/organisms/PushReminderDrawer';
import { StudentWelcomeOnboarding } from '@/components/organisms/StudentWelcomeOnboarding';
import { StudentFeedbackSurvey } from '@/components/organisms/StudentFeedbackSurvey';
import { ROUTES, buildRoute } from '@/constants/routes';
import {
  House,
  Headphones,
  MessagesSquare,
  ClockFading,
  Trophy,
} from 'lucide-react';
import { studentI18n, setStudentDocumentLang } from '@/i18n/studentI18n';
import { getUserInitials } from '@/utils/getUserInitials';
import { ensurePushForCurrentUser } from '@/platform/push';
import { useInitReminderTime } from '@/hooks/useInitReminderTime';

const STORY_ROUTE_PATTERN = /^\/student\/(?:story|result)\/[^/]+$/;
const RESULT_ROUTE_PATTERN = /^\/student\/result\/[^/]+$/;
/** Detalle Conversaciones — prácticas guiadas (paralelo al reto). */
const CONVERSATION_DETAIL_ROUTE_PATTERN = /^\/student\/conversations\/[^/]+$/;
/** Lista o detalle de palabras aprendidas (desde Progreso). */
const PROGRESS_LEARNED_WORDS_LIST_PATTERN = /^\/student\/progress\/learned-words\/([^/]+)$/;
const PROGRESS_LEARNED_WORD_DETAIL_PATTERN =
  /^\/student\/progress\/learned-words\/([^/]+)\/word\/[^/]+$/;
const PROGRESS_SAVED_VOCAB_DETAIL_PATTERN =
  /^\/student\/progress\/learned-words\/([^/]+)\/saved\/[^/]+$/;
/** Ficha de palabra guardada desde la página independiente (/saved-words/:id). */
const SAVED_WORD_DETAIL_PATTERN = /^\/student\/saved-words\/[^/]+$/;
/** Detalle de una recompensa (/rewards/:id). Mismo trato (compacto + nav). */
const REWARD_DETAIL_PATTERN = /^\/student\/rewards\/[^/]+$/;

const STUDENT_STORY_PATH = /^\/student\/story\/[^/]+$/;
const STORY_RESULT_ID_EXTRACT = /\/student\/(?:story|result)\/([^/]+)/;
const CONVERSATION_ID_EXTRACT = /\/student\/conversations\/([^/]+)/;

/** Solo resultado → detalle de actividad: navegación sin bloqueo. Detalle → resultado pasa por el blocker
 *  para que la cortina suba mientras sigue montada la pantalla anterior (historia / conversación). */
function isUnblockedReturnFromResult(from: string, to: string): boolean {
  return (
    RESULT_ROUTE_PATTERN.test(from) &&
    (STUDENT_STORY_PATH.test(to) || CONVERSATION_DETAIL_ROUTE_PATTERN.test(to))
  );
}

function StudentLocaleHtmlLang() {
  const { i18n } = useTranslation();
  useEffect(() => {
    const apply = (lng: string) => {
      setStudentDocumentLang(lng.startsWith('en') ? 'en' : 'es');
    };
    apply(i18n.language);
    i18n.on('languageChanged', apply);
    return () => {
      i18n.off('languageChanged', apply);
    };
  }, [i18n]);
  return null;
}

function StudentAreaContent() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { unreadCount, refreshCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const navGuard = useProfileNavGuard();
  const { xp } = useXP();
  // Mientras el modal de bienvenida esté abierto, suprimimos el push reminder
  // para que no se solapen en el primer acceso del alumno.
  const [welcomeOnboardingOpen, setWelcomeOnboardingOpen] = useState(false);

  useScreenWakeLock(Boolean(user));

  // Re-vincula la subscription push de este dispositivo al usuario actual en
  // cada carga del área de estudiante (rebind-on-login). Cubre la rotación de
  // endpoint y evita que un dispositivo entregue push a la cuenta equivocada.
  useEffect(() => {
    if (user) void ensurePushForCurrentUser();
  }, [user]);

  // Primera vez del alumno: registra la hora de recordatorio a la hora de
  // entrada (editable luego en Ajustes).
  useInitReminderTime();

  const userInitials = user?.name ? getUserInitials(user.name) : '??';

  const isChallengeRoute = STORY_ROUTE_PATTERN.test(location.pathname);
  const isChallengeResultRoute = RESULT_ROUTE_PATTERN.test(location.pathname);
  const isConversationsSectionDetailRoute = CONVERSATION_DETAIL_ROUTE_PATTERN.test(location.pathname);
  const learnedWordDetailPeriod = location.pathname.match(PROGRESS_LEARNED_WORD_DETAIL_PATTERN)?.[1];
  const isLearnedWordDetailRoute = !!learnedWordDetailPeriod;
  const savedVocabDetailPeriod = location.pathname.match(PROGRESS_SAVED_VOCAB_DETAIL_PATTERN)?.[1];
  const isSavedVocabDetailRoute = !!savedVocabDetailPeriod;
  const learnedWordsListPeriod = location.pathname.match(PROGRESS_LEARNED_WORDS_LIST_PATTERN)?.[1];
  const isLearnedWordsListRoute =
    !!learnedWordsListPeriod && !isLearnedWordDetailRoute && !isSavedVocabDetailRoute;
  // Ficha de palabra guardada: cabecera compacta con "atrás" + barra inferior visible.
  const isSavedWordDetailRoute = SAVED_WORD_DETAIL_PATTERN.test(location.pathname);
  // Detalle de recompensa: cabecera compacta con "atrás", SIN barra de navegación
  // (el canje va anclado abajo en su propia barra inferior).
  const isRewardDetailRoute = REWARD_DETAIL_PATTERN.test(location.pathname);
  const isLearnedWordsProgressRoute =
    isLearnedWordDetailRoute ||
    isSavedVocabDetailRoute ||
    isLearnedWordsListRoute ||
    isSavedWordDetailRoute;
  const isStudyTimeEvolutionRoute = location.pathname === ROUTES.STUDENT.PROGRESS_STUDY_TIME;
  const isMonthlyChallengesRoute = location.pathname === ROUTES.STUDENT.PROGRESS_STORIES_MONTH;
  const isProgressSubpageRoute = isStudyTimeEvolutionRoute || isMonthlyChallengesRoute;
  const isReviewRoute = location.pathname === ROUTES.STUDENT.REVIEW;
  const isLevelTestRoute = location.pathname === ROUTES.STUDENT.LEVEL_TEST;
  const isOnboardingPreferencesRoute =
    location.pathname === ROUTES.STUDENT.ONBOARDING_PREFERENCES;
  // Llegada desde Ajustes (?mode=edit): la cabecera muestra "atrás" (y vuelve a
  // Ajustes); en el onboarding inicial sigue siendo "cerrar" (vuelve al Dashboard).
  const isOnboardingPrefsEditMode =
    isOnboardingPreferencesRoute &&
    new URLSearchParams(location.search).get('mode') === 'edit';
  const isProfileRoute = location.pathname === ROUTES.STUDENT.PROFILE;
  const isSettingsRoute = location.pathname === ROUTES.STUDENT.SETTINGS;
  const isSupportRoute = location.pathname === ROUTES.STUDENT.SUPPORT;
  /** Header móvil compacto (botón atrás en lugar de XP/avatar). Perfil y Ajustes
   *  quedan FUERA: usan el header del dashboard y se vuelve por la barra inferior. */
  const isCompactMobileHeaderRoute =
    isReviewRoute ||
    isLevelTestRoute ||
    isLearnedWordsProgressRoute ||
    isProgressSubpageRoute ||
    isOnboardingPreferencesRoute ||
    isRewardDetailRoute;
  const isFullScreenLayoutRoute = isChallengeRoute || isConversationsSectionDetailRoute;
  // Páginas secundarias accesibles desde el menú burger: ocultan la navbar
  // inferior en móvil (se navega a ellas desde el menú, no desde las pestañas).
  const isMenuSecondaryRoute =
    isProfileRoute ||
    isSettingsRoute ||
    isSupportRoute ||
    isSavedWordDetailRoute ||
    location.pathname === ROUTES.STUDENT.NOTIFICATIONS ||
    location.pathname === ROUTES.STUDENT.INVITE ||
    location.pathname === ROUTES.STUDENT.REWARDS ||
    location.pathname === ROUTES.STUDENT.SAVED_WORDS;

  // Desktop: Stories en 2ª posición. Mobile: Stories en 3ª (IconSidebar reordena)
  const sidebarItems = useMemo(
    () =>
      [
        { id: 'home', icon: House, label: t('nav.home'), path: ROUTES.STUDENT.DASHBOARD },
        { id: 'stories', icon: Headphones, label: t('nav.stories'), path: ROUTES.STUDENT.STORIES },
        {
          id: 'conversations',
          icon: MessagesSquare,
          label: t('nav.conversations'),
          path: ROUTES.STUDENT.CONVERSATIONS,
        },
        { id: 'progress', icon: ClockFading, label: t('nav.progress'), path: ROUTES.STUDENT.PROGRESS },
        { id: 'ranking', icon: Trophy, label: t('nav.ranking'), path: ROUTES.STUDENT.RANKING },
      ],
    [t]
  );

  const displayName = user?.name || t('common.studentFallbackName');

  // Scroll to top on page change (except when returning from a story / review —
  // allows the target page to restore its own scroll).
  useEffect(() => {
    if (!isFullScreenLayoutRoute) {
      const state = location.state as
        | { returnFromStory?: boolean; returnFromReview?: boolean }
        | undefined;
      if (!state?.returnFromStory && !state?.returnFromReview) {
        window.scrollTo(0, 0);
      }
    }
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [location.pathname, isFullScreenLayoutRoute]);

  // Scroll to top on story/result/conversation-detail route change
  useEffect(() => {
    if (isFullScreenLayoutRoute) {
      const scrollContainer = document.querySelector('[data-story-scroll-container]');
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    }
  }, [location.pathname, isFullScreenLayoutRoute]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false;
    if (isUnblockedReturnFromResult(currentLocation.pathname, nextLocation.pathname)) return false;
    return (
      currentLocation.pathname.startsWith('/student') &&
      nextLocation.pathname.startsWith('/student')
    );
  });

  // Navegación que respeta el guard de cambios sin guardar (Perfil/Ajustes): si la
  // página actual tiene cambios pendientes, pide confirmación antes de salir. Antes
  // lo hacía el botón "atrás"; ahora que se vuelve por la barra inferior/menú, las
  // salidas pasan por aquí.
  const navigateWithGuard = (run: () => void) => {
    const guard = navGuard.getGuard();
    if (guard && guard.isDirty()) {
      guard.confirmLeave(run);
    } else {
      run();
    }
  };

  // Abre el perfil guardando solo de dónde venimos, para que su botón "volver"
  // devuelva a esa página (en limpio: sin restaurar scroll ni estado previo).
  const goToProfile = () => {
    navigateWithGuard(() =>
      navigate(ROUTES.STUDENT.PROFILE, {
        state: { profileFrom: location.pathname },
      })
    );
  };

  // Ayuda: abre el centro de ayuda externo o, si no hay URL, lleva a Ajustes.
  const handleHelp = () => {
    const url = import.meta.env.VITE_HELP_CENTER_URL?.trim();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(ROUTES.STUDENT.SETTINGS);
    }
  };

  /**
   * true si hay una entrada anterior del propio SPA en el historial: React
   * Router (data router) mantiene `idx` en history.state — 0 es la primera
   * entrada de la sesión (deep link, recarga en frío o arranque de la app).
   */
  const hasInAppHistory = () =>
    ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;

  /**
   * Vuelta desde una subpágina/detalle: volver de verdad en el historial
   * (restaura la entrada anterior con su scroll/estado) y, si no hay historial
   * (entrada directa), ir al padre con `replace` para no apilar entradas.
   *
   * NUNCA hacer aquí `navigate(padre)` a secas: el push deja el detalle como
   * entrada anterior y el siguiente «volver» de la lista (navigate(-1))
   * regresa a él — bucle detalle ⇄ lista del que no se puede salir
   * (detectado con Recompensas y Tu vocabulario, 2026-08-27).
   */
  const backOr = (fallback: string) => {
    if (hasInAppHistory()) navigate(-1);
    else navigate(fallback, { replace: true });
  };

  const doClose = () => {
    if (isLevelTestRoute) {
      navigate(ROUTES.STUDENT.DASHBOARD);
      return;
    }
    if (isSupportRoute) {
      // Soporte se abre desde el menú: volver a la página anterior.
      backOr(ROUTES.STUDENT.DASHBOARD);
      return;
    }
    if (isProgressSubpageRoute) {
      navigate(ROUTES.STUDENT.PROGRESS);
      return;
    }
    if (isOnboardingPreferencesRoute) {
      if (isOnboardingPrefsEditMode) backOr(ROUTES.STUDENT.SETTINGS);
      else navigate(ROUTES.STUDENT.DASHBOARD);
      return;
    }
    if (isSettingsRoute) {
      backOr(ROUTES.STUDENT.DASHBOARD);
      return;
    }
    if (isProfileRoute) {
      const ps = location.state as { profileFrom?: string } | null;
      const from = ps?.profileFrom;
      const doLeave = () => {
        // Volver del perfil a Historias / Conversaciones debe sentirse como una
        // navegación nueva (scroll arriba + animaciones de entrada desde cero),
        // igual que al entrar desde el Dashboard. Por eso NO restauramos scroll
        // ni reusamos el estado anterior de la lista: navegamos en limpio.
        if (from === ROUTES.STUDENT.STORIES || from === ROUTES.STUDENT.CONVERSATIONS) {
          navigate(from);
        } else {
          // Última página visitada (vuelta real en el historial).
          backOr(ROUTES.STUDENT.DASHBOARD);
        }
      };
      doLeave();
      return;
    }
    const state = location.state as {
      fromStoriesTab?: 'weekly' | 'all';
      fromDashboard?: boolean;
      fromReview?: boolean;
      resourceId?: string;
      scrollY?: number;
      viewMode?: 'card' | 'list';
      allAssignmentsNextOffset?: number;
      allAssignmentsHasMore?: boolean;
      reviewDueLoadedCount?: number;
      reviewDueHasMore?: boolean;
      forYouLoaded?: number;
      moreLoaded?: number;
    } | null;

    if (isRewardDetailRoute) {
      backOr(ROUTES.STUDENT.REWARDS);
    } else if (isSavedWordDetailRoute) {
      backOr(ROUTES.STUDENT.SAVED_WORDS);
    } else if (savedVocabDetailPeriod) {
      navigate(buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period: savedVocabDetailPeriod }), {
        state: { learnedWordsSegment: 'saved' as const },
      });
    } else if (learnedWordDetailPeriod) {
      navigate(buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period: learnedWordDetailPeriod }));
    } else if (isLearnedWordsListRoute && learnedWordsListPeriod) {
      navigate(ROUTES.STUDENT.PROGRESS);
    } else if (state?.fromReview) {
      const challengeOrResultId = location.pathname.match(STORY_RESULT_ID_EXTRACT)?.[1];
      if (challengeOrResultId) {
        navigate(ROUTES.STUDENT.REVIEW, {
          state: {
            returnFromReview: true,
            scrollToReviewSourceType: 'story' as const,
            scrollToReviewSourceId: challengeOrResultId,
            scrollY: state.scrollY,
            viewMode: state.viewMode,
            reviewDueLoadedCount: state.reviewDueLoadedCount,
            reviewDueHasMore: state.reviewDueHasMore,
          },
        });
      } else {
        const resourceId = isConversationsSectionDetailRoute
          ? location.pathname.match(CONVERSATION_ID_EXTRACT)?.[1]
          : state?.resourceId;
        navigate(ROUTES.STUDENT.REVIEW, {
          state: {
            returnFromReview: true,
            scrollToReviewSourceType: 'conversation' as const,
            scrollToReviewSourceId: resourceId,
            scrollY: state.scrollY,
            viewMode: state.viewMode,
            reviewDueLoadedCount: state.reviewDueLoadedCount,
            reviewDueHasMore: state.reviewDueHasMore,
          },
        });
      }
    } else if (isConversationsSectionDetailRoute) {
      const resourceId = location.pathname.match(CONVERSATION_ID_EXTRACT)?.[1];
      navigate(ROUTES.STUDENT.CONVERSATIONS, {
        state: {
          scrollToResourceId: resourceId,
          scrollY: state?.scrollY,
          viewMode: state?.viewMode,
          // Restaurar las páginas cargadas en cada sección (igual que Historias).
          returnFromConversation: true,
          forYouLoaded: state?.forYouLoaded,
          moreLoaded: state?.moreLoaded,
        },
      });
    } else if (state?.fromDashboard) {
      navigate(ROUTES.STUDENT.DASHBOARD, { state: { returnFromStory: true } });
    } else if (state?.fromStoriesTab) {
      const storyId = location.pathname.match(STORY_RESULT_ID_EXTRACT)?.[1];
      navigate(ROUTES.STUDENT.STORIES, {
        state: {
          tab: state.fromStoriesTab,
          scrollToStoryId: storyId,
          scrollY: state.scrollY,
          returnFromStory: true,
          viewMode: state.viewMode,
          // Restaurar las páginas cargadas de cada subsección del grid "all".
          ...(state.fromStoriesTab === 'all'
            ? {
                forYouLoaded: state.forYouLoaded,
                moreLoaded: state.moreLoaded,
              }
            : {}),
        },
      });
    } else if (location.pathname === ROUTES.STUDENT.REVIEW) {
      navigate(ROUTES.STUDENT.DASHBOARD);
    } else {
      backOr(ROUTES.STUDENT.DASHBOARD);
    }
  };

  // El cierre (X del header) respeta el guard de la página activa (Perfil,
  // Ajustes, actividades de historia/conversación): si hay trabajo sin guardar,
  // la página muestra su modal de confirmación antes de salir.
  const handleClose = () => {
    const guard = navGuard.getGuard();
    if (guard && guard.isDirty()) {
      guard.confirmLeave(doClose);
      return;
    }
    doClose();
  };

  // IconSidebar SIEMPRE montado — nunca se desmonta al cambiar de layout.
  // En rutas full-screen (historia/conversación), se oculta con hideOnMobile y pointer-events:none.
  // Las subpáginas de segundo nivel que cuelgan de Progreso (evolución de tiempo,
  // retos del mes, vocabulario aprendido lista/detalle, guardado) OCULTAN la navbar
  // en móvil: se navega a ellas desde Progreso y se vuelve con el botón "atrás".
  // Excepción: la ficha de palabra guardada (/saved-words/:savedId) NO cuelga de
  // Progreso y mantiene la navbar.
  // Al volver, Framer Motion anima desde y:150 → y:0 sin re-montar.
  const hideIconSidebarOnMobile =
    isFullScreenLayoutRoute ||
    isMenuSecondaryRoute ||
    (isCompactMobileHeaderRoute && !isSavedWordDetailRoute);

  return (
    <StoryBottomBarProvider>
      <PushReminderDrawer suppress={welcomeOnboardingOpen} />
      <StudentWelcomeOnboarding onOpenChange={setWelcomeOnboardingOpen} />
      <StudentFeedbackSurvey suppress={welcomeOnboardingOpen} />
      <IconSidebar
        items={sidebarItems}
        activePath={location.pathname}
        onNavigate={(path) => navigateWithGuard(() => navigate(path))}
        hideOnMobile={hideIconSidebarOnMobile}
        moreNavOrder={['home', 'stories', 'conversations', 'ranking', 'progress']}
      />
      <PageTransition pathname={location.pathname} blocker={blocker}>
        {isFullScreenLayoutRoute ? (
          <StoryAudioProvider>
            <StoryLayout
              userName={displayName}
              userInitials={userInitials}
              xp={xp}
              notificationCount={unreadCount}
              onNotificationClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.NOTIFICATIONS))}
              onCloseClick={isChallengeResultRoute ? undefined : handleClose}
              onSavedItemsClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.SAVED_WORDS))}
              onProfileClick={goToProfile}
              onSettingsClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.SETTINGS))}
              onLogout={logout}
              onDropdownOpen={refreshCount}
            >
              <Outlet />
            </StoryLayout>
          </StoryAudioProvider>
        ) : (
          <StudentDashboardLayout
            userName={displayName}
            userInitials={userInitials}
            xp={xp}
            notificationCount={unreadCount}
            sidebarItems={sidebarItems}
            activePath={location.pathname}
            onNavigate={(path) => navigateWithGuard(() => navigate(path))}
            onNotificationClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.NOTIFICATIONS))}
            onSavedItemsClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.SAVED_WORDS))}
            onProfileClick={goToProfile}
            onSettingsClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.SETTINGS))}
            onLogout={logout}
            onDropdownOpen={refreshCount}
            hideSidebarOnMobile={isCompactMobileHeaderRoute || isMenuSecondaryRoute}
            compactSuppressBottomNavPadding={
              (isCompactMobileHeaderRoute && !isSavedWordDetailRoute) ||
              isMenuSecondaryRoute
            }
            compactCloseOnDesktop={
              isLearnedWordsProgressRoute || isProgressSubpageRoute || isLevelTestRoute
            }
            onlyBackHeader={isProgressSubpageRoute}
            onCloseClick={isCompactMobileHeaderRoute || isMenuSecondaryRoute ? handleClose : undefined}
            compactButtonVariant={
              isMenuSecondaryRoute ||
              isLearnedWordDetailRoute ||
              isSavedVocabDetailRoute ||
              isSavedWordDetailRoute ||
              isRewardDetailRoute ||
              isReviewRoute ||
              isLearnedWordsListRoute ||
              isProgressSubpageRoute ||
              isOnboardingPrefsEditMode
                ? 'back'
                : 'close'
            }
            showInviteDrawerItem={location.pathname === ROUTES.STUDENT.DASHBOARD}
            onInviteClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.INVITE))}
            onRewardsClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.REWARDS))}
            onXpClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.REWARDS))}
            onLanguageClick={() =>
              navigate({ pathname: ROUTES.STUDENT.SETTINGS, hash: 'settings-interface' })
            }
            onHelpClick={handleHelp}
            onSupportClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.SUPPORT))}
            onLevelTestClick={() => navigateWithGuard(() => navigate(ROUTES.STUDENT.LEVEL_TEST))}
          >
            <Outlet />
          </StudentDashboardLayout>
        )}
      </PageTransition>
    </StoryBottomBarProvider>
  );
}

export function StudentAreaWrapper() {
  return (
    <XPProvider>
      <I18nextProvider i18n={studentI18n}>
        <StudentLocaleHtmlLang />
        <ProfileNavGuardProvider>
          <StudentAreaContent />
        </ProfileNavGuardProvider>
      </I18nextProvider>
    </XPProvider>
  );
}

StudentAreaWrapper.displayName = 'StudentAreaWrapper';
